import { useState, useRef, useEffect } from 'react'
import { Terminal, Play, Square, RotateCcw } from 'lucide-react'

interface Command {
  id: string
  command: string
  output: string
  timestamp: Date
  status: 'success' | 'error' | 'running'
}

function TerminalInterface() {
  const [commands, setCommands] = useState<Command[]>([])
  const [input, setInput] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const terminalRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    terminalRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [commands])

  const executeCommand = async () => {
    if (!input.trim() || isRunning) return

    const cmd = input.trim()
    const command: Command = {
      id: Date.now().toString(),
      command: cmd,
      output: '',
      timestamp: new Date(),
      status: 'running'
    }

    setCommands(prev => [...prev, command])
    setInput('')
    setIsRunning(true)

    try {
      // Execute the command for real through the MeesteR agent bridge
      const response = await fetch('/api/terminal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: cmd,
          timeout: 60,
        }),
      })

      const result = await response.json()

      let output = ''
      if (result.stdout) output += result.stdout
      if (result.stderr) output += (output ? '\n' : '') + result.stderr

      if (!output && result.returncode === 0) {
        output = '(command completed with no output)'
      }

      setCommands(prev => prev.map(cmd =>
        cmd.id === command.id
          ? {
              ...cmd,
              output,
              status: result.returncode === 0 ? 'success' : 'error',
            }
          : cmd
      ))
    } catch (error) {
      setCommands(prev => prev.map(cmd =>
        cmd.id === command.id
          ? { ...cmd, output: `Error: ${error}`, status: 'error' }
          : cmd
      ))
    } finally {
      setIsRunning(false)
    }
  }

  const clearTerminal = () => {
    setCommands([])
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      executeCommand()
    }
  }

  const quickCommands = [
    { label: 'System Status', command: 'uname -a && uptime' },
    { label: 'Disk Usage', command: 'df -h /' },
    { label: 'Clear', command: 'clear' },
    { label: 'Version', command: 'python3 --version && node --version' }
  ]

  return (
    <div className="h-full flex flex-col bg-secondary-900">
      {/* Terminal Header */}
      <div className="bg-secondary-800 border-b border-secondary-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Terminal className="w-6 h-6 text-accent-400" />
            <h2 className="text-xl font-semibold">System Terminal</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={clearTerminal}
              className="flex items-center space-x-2 px-3 py-2 bg-secondary-700 hover:bg-secondary-600 text-secondary-200 rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Commands */}
      <div className="bg-secondary-800 border-b border-secondary-700 px-6 py-3">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-secondary-400">Quick Commands:</span>
          {quickCommands.map((quickCmd, index) => (
            <button
              key={index}
              onClick={() => {
                setInput(quickCmd.command)
                setTimeout(() => executeCommand(), 100)
              }}
              className="px-3 py-1 bg-secondary-700 hover:bg-secondary-600 text-secondary-200 rounded text-sm transition-colors"
            >
              {quickCmd.label}
            </button>
          ))}
        </div>
      </div>

      {/* Terminal Output */}
      <div className="flex-1 overflow-y-auto bg-black p-4 font-mono text-sm">
        {commands.length === 0 ? (
          <div className="h-full flex items-center justify-center text-secondary-400">
            <div className="text-center">
              <Terminal className="w-16 h-16 mx-auto mb-4 text-secondary-600" />
              <h3 className="text-xl font-semibold mb-2">System Terminal</h3>
              <p>Enter commands to interact with the AI system</p>
            </div>
          </div>
        ) : (
          commands.map((cmd) => (
            <div key={cmd.id} className="mb-4">
              {/* Command Input */}
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-green-400">$</span>
                <span className="text-white">{cmd.command}</span>
                {cmd.status === 'running' && (
                  <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                )}
              </div>
              
              {/* Command Output */}
              {cmd.output && (
                <pre className={`whitespace-pre-wrap ${
                  cmd.status === 'error' ? 'text-red-400' : 'text-secondary-300'
                }`}>
                  {cmd.output}
                </pre>
              )}
              
              {/* Status Indicator */}
              <div className="text-xs text-secondary-500 mt-1">
                {cmd.timestamp.toLocaleTimeString()} • 
                {cmd.status === 'success' && ' ✅ Success'}
                {cmd.status === 'error' && ' ❌ Error'}
                {cmd.status === 'running' && ' ⏳ Running...'}
              </div>
            </div>
          ))
        )}
        
        {/* Command Input Area */}
        <div className="flex items-center space-x-2 mt-4">
          <span className="text-green-400">$</span>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter system command..."
            className="flex-1 bg-transparent border-none outline-none text-white placeholder-secondary-400"
            disabled={isRunning}
          />
          <button
            onClick={executeCommand}
            disabled={!input.trim() || isRunning}
            className={`flex items-center space-x-2 px-3 py-1 rounded transition-colors ${
              isRunning
                ? 'bg-yellow-500 text-black'
                : 'bg-green-500 hover:bg-green-600 text-white disabled:bg-secondary-600 disabled:cursor-not-allowed'
            }`}
          >
            {isRunning ? (
              <>
                <Square className="w-4 h-4" />
                <span>Stop</span>
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                <span>Run</span>
              </>
            )}
          </button>
        </div>
        
        <div ref={terminalRef} />
      </div>

      {/* Footer Info */}
      <div className="bg-secondary-800 border-t border-secondary-700 px-6 py-2">
        <div className="flex items-center justify-between text-xs text-secondary-400">
          <span>Connected to AI WizzarD System</span>
          <span>Terminal v1.0 • {commands.length} commands executed</span>
        </div>
      </div>
    </div>
  )
}

export default TerminalInterface
